/**
 * V111: MM 프로젝트 테이블 컴포넌트
 * V114.4: 등급별 인원 카운트 컬럼 추가
 * V115: 기간별 행(period rows) 표시 - 리비전 segment별 MD 계산
 * V116: 테이블 컬럼 순서 변경 + 변경 음영 처리 + 날짜 폰트 크기 증가
 * V116.1: UI 회귀 수정
 * V116.1.4: 컬럼 너비 +10 확대 + 텍스트 크기 +2 확대
 * V116.5: 프로젝트명 클릭 시 상세 Drawer 표시 (읽기 전용)
 * V128: 프로젝트 블록 4행 구조
 *   - 블록 1행: 헤더1 (기간|WD|계약|등급별|투입|등급별|기준MD|실투입MD|기준MM|실투입MM)
 *   - 블록 2~N+1행: 리비전 데이터
 *   - 블록 N+2행: 헤더2 (추가근무MD|등급별추가근무MD|업무상세|특이사항|테스트 결제비용)
 *   - 블록 N+3행: 값2 (OT + 메모 값)
 *   - 프로젝트명 rowSpan = n + 3
 * V129.4: 헤더1/헤더2 완전 독립 너비
 *   - 헤더2/값2를 단일 td(colSpan=18) + 내부 flex 레이아웃으로 변경
 *   - td 자체가 header1과 완전히 독립적인 너비를 가짐
 * V130: 개별 필드 편집 모달
 *   - 팀 단위 저장 버튼 제거
 *   - 업무상세/특이사항/테스트 결제비용 → 읽기 전용 클릭 영역
 *   - 클릭 시 개별 편집 모달 오픈
 *   - 모달에서 해당 필드만 patch 저장
 *
 * 테이블 구조:
 * 프로젝트명 | 기간 | WD | 계약 | 특 | 고 | 중 | 초 | 무 | 투입 | 특 | 고 | 중 | 초 | 기준MD | 실투입MD | 기준MM | 실투입MM | 업무상세 | 특이사항 | 테스트 결제비용
 *
 * V205: 기준MM/실투입MM 컬럼 추가
 *   - MD를 MM으로 환산 (기본 20일 = 1MM)
 *   - 환산 정책은 mmConversion.ts에서 관리 (향후 WD 기반 확장 가능)
 * V205.1: MM 컬럼 "프로젝트 요약 1행"에만 표시
 *   - 기존: 모든 리비전 행에 MM 반복 표시 → 수정: 첫 번째 리비전 행에서만 rowSpan으로 표시
 *   - 리비전 무관 전체 합산값 (project.baseMd / 20, project.actualMd / 20)
 * V205.2: 안정화
 *   - COLS_EXCLUDING_PROJECT_NAME, MM_COLS, COLS_EXCLUDING_MM 상수 정의
 *   - visibleSegments 기반 rowSpan 계산 (향후 필터링 확장 가능)
 *   - colSpan 하드코딩 제거 → 상수 참조
 */

import React, { useState, useCallback } from 'react';
import { MmProjectData, ContractGradeCounts, OvertimeByGrade } from '../../../shared/types/mm';
import { MmNoteEditModal, MmNoteFieldType } from './MmNoteEditModal';
// V116.6: 통합 Drawer 사용 (readOnly 모드)
import { ProjectDetailDrawer } from '../../org-management/OrgProjectsPage';
// V205: MD → MM 환산 유틸리티
// V207.1: WD 기반 MM 환산 함수 (OT는 BE에서 이미 actualMd에 포함)
import { formatMmByWd } from '../../../shared/utils/mmConversion';

// ============================================
// V205.2: 컬럼 수 상수 (colSpan 정합성 유지용)
// ============================================

/**
 * V205.2: 프로젝트명 제외 전체 컬럼 수
 *
 * 헤더1 컬럼 구성 (프로젝트명 제외):
 * - 적용기간(1) + WD(1) + 계약(1) + 등급별(5) + 투입(1) + 등급별(4)
 * - 기준MD(1) + 실투입MD(4) + 기준MM(1) + 실투입MM(1)
 * = 20
 *
 * 헤더2/값2는 단일 td(colSpan=COLS_EXCLUDING_PROJECT_NAME) + flex 레이아웃
 */
const COLS_EXCLUDING_PROJECT_NAME = 20;

/**
 * V205.2: MM 컬럼 수 (기준MM + 실투입MM)
 */
const MM_COLS = 2;

/**
 * V205.2: MM 제외 컬럼 수 (리비전 없음 텍스트 영역)
 */
const COLS_EXCLUDING_MM = COLS_EXCLUDING_PROJECT_NAME - MM_COLS;

interface MmProjectTableProps {
  /** 프로젝트 목록 */
  projects: MmProjectData[];
  /** 팀 ID */
  teamId: string;
  /** 대상 월 */
  month: string;
  /** 편집 가능 여부 (mm/register 페이지) */
  isEditable?: boolean;
  /** 메모 저장 핸들러 (V130: 개별 필드 저장) */
  onSaveNote?: (
    teamId: string,
    projectId: string,
    notes: { workDetails?: string; remarks?: string; costOccurred?: string }
  ) => Promise<void>;
  /** 저장 중 상태 */
  isSaving?: boolean;
  /**
   * V207: 월 근무일수 (WD 기반 MM 환산용)
   *
   * 해당 월의 총 근무일수 (주말+공휴일 제외)
   * 0 또는 undefined면 MM 컬럼에 '-' 표시
   */
  monthWorkingDays?: number;
}

/**
 * V130: 모달 상태
 */
interface ModalState {
  isOpen: boolean;
  field: MmNoteFieldType;
  projectId: string;
  projectName: string;
  initialValue: string;
}

/**
 * V116.1: 등급 카운트 포맷팅 (0이면 '-', 그 외 숫자)
 */
function formatGradeCount(count: number | undefined): string {
  if (count === undefined || count === 0) return '-';
  return String(count);
}

/**
 * V116: 기간 포맷팅 (MM.DD ~ MM.DD)
 */
function formatPeriod(from: string, to: string): string {
  const fromPart = from.substring(5).replace('-', '.'); // MM.DD
  const toPart = to.substring(5).replace('-', '.');
  return `${fromPart} ~ ${toPart}`;
}

/**
 * V128: 총 OT MD 계산
 * V128.3: 값이 없으면 빈 문자열 반환
 * V130.2: 값이 0이면 '0' 표시
 * V206.2: 등급별 MD를 각각 반올림한 후 합산 (반올림 오차 해소)
 *         → 등급별 추가근무MD 합계와 추가근무MD가 일치하도록 보장
 */
function getTotalOvertimeMd(otByGrade: OvertimeByGrade | undefined): string {
  if (!otByGrade) return '0';

  const gradeKeys: (keyof OvertimeByGrade)[] = ['special', 'high', 'mid', 'low', 'none'];
  let totalMd = 0;

  for (const key of gradeKeys) {
    const minutes = otByGrade[key] || 0;
    if (minutes > 0) {
      // 각 등급별로 반올림(toFixed(2))하여 합산
      totalMd += Math.round((minutes / 60 / 8) * 100) / 100;
    }
  }

  if (totalMd === 0) return '0';
  return totalMd.toFixed(2);
}

/**
 * V128.2: 등급별 OT MD 멀티라인 포맷팅
 * 값이 있는 등급만 줄바꿈으로 표시 (특급 → 고급 → 중급 → 초급 → 등급무관 순)
 * V128.3: 값이 없으면 null 반환 (빈 셀)
 * V130.2: 값이 없으면 '-' 표시
 */
function formatOvertimeByGradeMultiline(otByGrade: OvertimeByGrade | undefined): React.ReactNode {
  if (!otByGrade) return '-';

  const gradeOrder: { key: keyof OvertimeByGrade; label: string }[] = [
    { key: 'special', label: '특급' },
    { key: 'high', label: '고급' },
    { key: 'mid', label: '중급' },
    { key: 'low', label: '초급' },
    { key: 'none', label: '등급무관' },
  ];

  const entries: { label: string; md: number }[] = [];
  for (const { key, label } of gradeOrder) {
    const minutes = otByGrade[key] || 0;
    if (minutes > 0) {
      entries.push({ label, md: minutes / 60 / 8 });
    }
  }

  if (entries.length === 0) return '-';

  return (
    <>
      {entries.map((e, i) => (
        <React.Fragment key={e.label}>
          {i > 0 && <br />}
          {e.label} {e.md.toFixed(2)}MD
        </React.Fragment>
      ))}
    </>
  );
}

/**
 * MM 프로젝트 테이블
 */
export function MmProjectTable({
  projects,
  teamId,
  month,
  isEditable = false,
  onSaveNote,
  isSaving = false,
  monthWorkingDays,
}: MmProjectTableProps) {
  // V116.5: 프로젝트 상세 Drawer 상태
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  /**
   * V210: MM 계산 (월 전체 WD 기반)
   *
   * 정책:
   * - 기준MM = project.baseMd / monthWD
   * - 실투입MM = project.actualMd / monthWD
   * - monthWD = 해당 월 전체 근무일수 (response.monthInfo.workingDays)
   *
   * 예시 (monthWD=20):
   * - 월 전체 덮는 프로젝트: baseMd=20(1명) → baseMM=1.00
   * - 월 내 WD 10만 포함: baseMd=10(1명) → baseMM=0.50
   *
   * OT 포함 여부 (SSoT: getMmOverviewData.ts Line 1905-1917):
   * - BE에서 project.actualMd = 실투입MD + 야근MD 이미 합산됨 (V126.1)
   * - FE에서 OT 추가 합산 금지
   */
  const getMm = useCallback(
    (project: MmProjectData): { baseMm: string; actualMm: string } => {
      const baseMm = formatMmByWd(project.baseMd, monthWorkingDays);
      const actualMm = formatMmByWd(project.actualMd, monthWorkingDays);

      return { baseMm, actualMm };
    },
    [monthWorkingDays]
  );

  // V130: 개별 편집 모달 상태
  const [modalState, setModalState] = useState<ModalState | null>(null);
  const [isModalSaving, setIsModalSaving] = useState(false);

  // V130: 필드 클릭 시 모달 열기
  const handleFieldClick = useCallback(
    (project: MmProjectData, field: MmNoteFieldType) => {
      if (!isEditable) return;

      const valueMap: Record<MmNoteFieldType, string> = {
        workDetails: project.notes.workDetails,
        remarks: project.notes.remarks,
        costOccurred: project.notes.costOccurred,
      };

      setModalState({
        isOpen: true,
        field,
        projectId: project.projectId,
        projectName: project.projectName,
        initialValue: valueMap[field],
      });
    },
    [isEditable]
  );

  // V130.1: 개별 필드 저장 핸들러 (중복 요청 방지 추가)
  const handleModalSave = useCallback(
    async (value: string) => {
      // V130.1: 중복 요청 방지 - 저장 중이면 early return
      if (isModalSaving) return;
      if (!onSaveNote || !modalState) return;

      setIsModalSaving(true);
      try {
        // 해당 필드만 patch 저장
        const notes: { workDetails?: string; remarks?: string; costOccurred?: string } = {};
        notes[modalState.field] = value;

        await onSaveNote(teamId, modalState.projectId, notes);
      } finally {
        setIsModalSaving(false);
      }
    },
    [teamId, modalState, onSaveNote, isModalSaving]
  );

  // V130: 모달 닫기
  const handleModalClose = useCallback(() => {
    setModalState(null);
  }, []);

  if (projects.length === 0) {
    return (
      <div
        style={{
          padding: '24px',
          textAlign: 'center',
          color: 'var(--color-gray-500)',
          fontSize: '14px',
        }}
      >
        진행중인 프로젝트가 없습니다.
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      {/* V130: 테이블 상단 헤더 - 제목만 (저장 버튼 제거) */}
      <div
        style={{
          padding: '0 0 12px',
          minHeight: '40px',
        }}
      >
        {/* 프로젝트 현황 제목 */}
        <h4
          style={{
            fontSize: '26px',
            fontWeight: 600,
            color: 'var(--color-gray-800)',
            margin: 0,
          }}
        >
          프로젝트 현황 ({projects.length}개)
        </h4>
      </div>

      <div style={{ overflowX: 'auto', width: '100%' }}>
        <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '13px',
        }}
      >
        {/* V128.4: 메인 thead 제거 - 각 프로젝트 블록에 자체 Header1/Header2 행이 있음 */}
        <tbody>
          {projects.map((project, projectIdx) => {
            // V205.2: visibleSegments 기반 rowSpan 계산 (향후 필터링 확장 가능)
            const segments = project.revisions || [];
            const visibleSegments = segments; // 현재는 전체, 향후 필터링 시 여기서 처리
            // V210: MM 계산 (월 전체 WD 기반)
            const { baseMm, actualMm } = getMm(project);
            // V128.6: 프로젝트 블록 구조 변경
            // Header1은 "프로젝트명" 헤더만 표시, 프로젝트명 값은 2행부터 rowSpan
            // rowSpan (프로젝트명 값) = 리비전행(n, 최소1) + 헤더2(1) + 값2(1) = n + 2
            // V205.2: visibleSegments.length 기반으로 rowSpan 계산
            const revisionRowCount = visibleSegments.length || 1;
            const projectNameRowSpan = revisionRowCount + 2;
            // V64-1A: 첫 프로젝트가 아니면 상단에 작은 공백 스페이서 행을 렌더링
            const isFirstProject = projectIdx === 0;

            return segments.length === 0 ? (
              // V128: 리비전 없는 경우 - 4행 블록 구조
              <React.Fragment key={project.projectId}>
                {/* V64-1A: 프로젝트 간 공백 스페이서 (첫 프로젝트 제외) */}
                {!isFirstProject && (
                  <tr aria-hidden="true">
                    <td colSpan={21} style={{ height: '16px', padding: 0, backgroundColor: 'var(--color-gray-50)', border: 'none' }} />
                  </tr>
                )}
                {/* 블록 1행: 헤더1 */}
                {/* V64-1B: 프로젝트마다 상/하/좌/우 2px gray-500 박스 테두리 */}
                <tr style={{ backgroundColor: 'var(--color-gray-100)', borderTop: '2px solid var(--color-gray-500)' }}>
                  {/* V128.6: 프로젝트명 헤더 */}
                  <td style={{ ...inlineThStyle, borderLeft: '2px solid var(--color-gray-500)', borderRight: '1px solid var(--color-gray-300)' }}>프로젝트명</td>
                  {/* 헤더1 셀들: 기간~실투입MD (업무상세/특이사항/테스트 결제비용은 헤더2에서만 표시) */}
                  <td style={{ ...inlineThStyle }}>적용기간</td>
                  <td style={{ ...inlineThStyle, borderLeft: '1px solid var(--color-gray-300)' }}>WD</td>
                  <td style={{ ...inlineThStyle, borderLeft: '1px solid var(--color-gray-300)' }}>계약</td>
                  <td style={{ ...inlineThStyle }}>특</td>
                  <td style={{ ...inlineThStyle }}>고</td>
                  <td style={{ ...inlineThStyle }}>중</td>
                  <td style={{ ...inlineThStyle }}>초</td>
                  <td style={{ ...inlineThStyle }}>무</td>
                  <td style={{ ...inlineThStyle, borderLeft: '1px solid var(--color-gray-300)' }}>투입</td>
                  <td style={{ ...inlineThStyle }}>특</td>
                  <td style={{ ...inlineThStyle }}>고</td>
                  <td style={{ ...inlineThStyle }}>중</td>
                  <td style={{ ...inlineThStyle }}>초</td>
                  <td style={{ ...inlineThStyle, borderLeft: '1px solid var(--color-gray-300)' }}>기준MD</td>
                  {/* V128.2: 실투입MD colSpan=4 (업무상세/특이사항/테스트 결제비용은 헤더2에서만 표시) */}
                  <td colSpan={4} style={{ ...inlineThStyle, borderLeft: '1px solid var(--color-gray-300)' }}>실투입MD</td>
                  {/* V205: 기준MM/실투입MM 컬럼 추가 (리비전 무관 전체 합산) */}
                  <td style={{ ...inlineThStyle, borderLeft: '1px solid var(--color-gray-300)' }}>기준MM</td>
                  <td style={{ ...inlineThStyle, borderLeft: '1px solid var(--color-gray-300)', borderRight: '2px solid var(--color-gray-500)' }}>실투입MM</td>
                </tr>

                {/* 블록 2행: 리비전 데이터 없음 */}
                {/* V64-1B: 데이터행 white 배경 + 박스 좌측 2px */}
                <tr style={{ backgroundColor: 'white', borderBottom: '1px solid var(--color-gray-200)' }}>
                  {/* V128.6: 프로젝트명 값 (rowSpan = 3: 데이터없음 + 헤더2 + 값2) */}
                  <td rowSpan={projectNameRowSpan} style={{ ...tdStyle, width: '180px', maxWidth: '180px', verticalAlign: 'middle', textAlign: 'center', borderLeft: '2px solid var(--color-gray-500)', borderRight: '1px solid var(--color-gray-300)', borderBottom: '2px solid var(--color-gray-500)' }}>
                    <button
                      type="button"
                      onClick={() => setSelectedProjectId(project.projectId)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '5px',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        textAlign: 'center',
                      }}
                      title={`${project.projectName} 상세 보기`}
                    >
                      <span style={{
                        color: 'var(--color-primary-dark)',
                        textDecoration: 'underline',
                        textDecorationColor: 'var(--color-primary-light)',
                        fontSize: '15px',
                        wordBreak: 'break-word',
                      }}>
                        {project.projectName}
                      </span>
                    </button>
                  </td>
                  {/* V205.2: 리비전 없어도 MM 컬럼은 요약 1행에 표시 (COLS_EXCLUDING_MM 상수 사용) */}
                  <td colSpan={COLS_EXCLUDING_MM} style={{ ...tdStyle, textAlign: 'center', color: 'var(--color-gray-400)', padding: '12px' }}>
                    리비전 데이터 없음
                  </td>
                  {/* V210: 기준MM (월 전체 WD 기반) */}
                  <td style={{
                    ...tdStyle,
                    textAlign: 'center',
                    padding: '8px 4px',
                    borderLeft: '1px solid var(--color-gray-300)',
                    fontWeight: 500,
                    color: 'var(--color-purple-600)',
                    verticalAlign: 'middle',
                  }}>
                    {baseMm}
                  </td>
                  {/* V210: 실투입MM (월 전체 WD 기반, OT 포함) */}
                  <td style={{
                    ...tdStyle,
                    textAlign: 'center',
                    fontWeight: 600,
                    padding: '8px 4px',
                    borderLeft: '1px solid var(--color-gray-300)',
                    borderRight: '2px solid var(--color-gray-500)',
                    color: project.actualMd > project.baseMd
                      ? 'var(--color-green-600)'
                      : project.actualMd < project.baseMd
                        ? 'var(--color-red-600)'
                        : 'var(--color-purple-600)',
                    verticalAlign: 'middle',
                  }}>
                    {actualMm}
                  </td>
                </tr>

                {/* V128.2: 블록 3행 헤더2 (추가근무MD | 등급별추가근무MD | 업무상세 | 특이사항 | 테스트 결제비용) */}
                {/* V129.4: td 자체 독립 너비를 위해 단일 td + flex 레이아웃 사용 */}
                {/* V132.1: 헤더-내용 세로 정렬 통일 (좌우 패딩 6px로 맞춤) */}
                <tr style={{ backgroundColor: 'var(--color-gray-100)' }}>
                  <td colSpan={20} style={{ padding: 0, borderRight: '2px solid var(--color-gray-500)' }}>
                    <div style={{ display: 'flex' }}>
                      <div style={{ width: '160px', ...inlineThStyle, padding: '6px 6px', boxSizing: 'border-box' }}>추가근무MD</div>
                      <div style={{ width: '200px', ...inlineThStyle, padding: '6px 6px', boxSizing: 'border-box' }}>등급별 추가근무MD</div>
                      <div style={{ flex: 5, ...inlineThStyle, padding: '6px 6px', boxSizing: 'border-box', borderLeft: '1px solid var(--color-gray-300)' }}>업무상세</div>
                      <div style={{ flex: 3, ...inlineThStyle, padding: '6px 6px', boxSizing: 'border-box', borderLeft: '1px solid var(--color-gray-300)' }}>특이사항</div>
                      <div style={{ flex: 2, ...inlineThStyle, padding: '6px 6px', boxSizing: 'border-box', borderLeft: '1px solid var(--color-gray-300)' }}>테스트 결제비용</div>
                    </div>
                  </td>
                </tr>

                {/* V128.3: 블록 4행 값2 */}
                {/* V129.4: td 자체 독립 너비를 위해 단일 td + flex 레이아웃 사용 */}
                {/* V130.2: 프로젝트 구분선 강화 - 하단 borderBottom 제거 (상단 borderTop으로 통일) */}
                {/* V132.1: boxSizing 추가로 헤더-내용 세로 정렬 통일 */}
                {/* V64-1B: 값2 행 white 배경 + 박스 우/하단 2px */}
                <tr style={{ backgroundColor: 'white' }}>
                  <td colSpan={20} style={{ padding: 0, borderRight: '2px solid var(--color-gray-500)', borderBottom: '2px solid var(--color-gray-500)' }}>
                    <div style={{ display: 'flex' }}>
                      {/* 추가근무MD (중앙정렬) */}
                      <div style={{ width: '160px', ...tdStyle, boxSizing: 'border-box', textAlign: 'center', whiteSpace: 'nowrap' }}>
                      </div>
                      {/* 등급별 추가근무MD (중앙정렬) */}
                      <div style={{ width: '200px', ...tdStyle, boxSizing: 'border-box', textAlign: 'center', whiteSpace: 'nowrap', fontSize: '18px' }}>
                      </div>
                      {/* V130: 업무상세 - 읽기 전용 클릭 영역 */}
                      {/* V204: memoTdStyle 적용 (기존 크기 유지) */}
                      <div
                        style={{
                          flex: 5,
                          ...memoTdStyle,
                          boxSizing: 'border-box',
                          textAlign: 'left',
                          verticalAlign: 'top',
                          borderLeft: '1px solid var(--color-gray-300)',
                          cursor: isEditable ? 'pointer' : undefined,
                          minHeight: '40px',
                          position: 'relative',
                          overflow: 'hidden',
                        }}
                        onClick={() => handleFieldClick(project, 'workDetails')}
                        title={isEditable ? '클릭하여 편집' : undefined}
                      >
                        {project.notes.workDetails ? (
                          <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {project.notes.workDetails}
                          </span>
                        ) : (
                          <span style={{ display: 'block', textAlign: 'center', color: 'var(--color-gray-400)' }}>
                            {isEditable ? '클릭하여 입력' : '-'}
                          </span>
                        )}
                      </div>
                      {/* V130: 특이사항 - 읽기 전용 클릭 영역 */}
                      {/* V204: memoTdStyle 적용 (기존 크기 유지) */}
                      <div
                        style={{
                          flex: 3,
                          ...memoTdStyle,
                          boxSizing: 'border-box',
                          textAlign: 'left',
                          verticalAlign: 'top',
                          borderLeft: '1px solid var(--color-gray-300)',
                          cursor: isEditable ? 'pointer' : undefined,
                          minHeight: '40px',
                          overflow: 'hidden',
                        }}
                        onClick={() => handleFieldClick(project, 'remarks')}
                        title={isEditable ? '클릭하여 편집' : undefined}
                      >
                        {project.notes.remarks ? (
                          <span style={{ color: 'var(--color-yellow-700)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {project.notes.remarks}
                          </span>
                        ) : (
                          <span style={{ display: 'block', textAlign: 'center', color: 'var(--color-gray-400)' }}>
                            {isEditable ? '클릭하여 입력' : '-'}
                          </span>
                        )}
                      </div>
                      {/* V130: 테스트 결제비용 - 읽기 전용 클릭 영역 */}
                      {/* V204: memoTdStyle 적용 (기존 크기 유지) */}
                      <div
                        style={{
                          flex: 2,
                          ...memoTdStyle,
                          boxSizing: 'border-box',
                          textAlign: 'left',
                          verticalAlign: 'top',
                          borderLeft: '1px solid var(--color-gray-300)',
                          cursor: isEditable ? 'pointer' : undefined,
                          minHeight: '40px',
                          overflow: 'hidden',
                        }}
                        onClick={() => handleFieldClick(project, 'costOccurred')}
                        title={isEditable ? '클릭하여 편집' : undefined}
                      >
                        {project.notes.costOccurred ? (
                          <span style={{ color: 'var(--color-green-700)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {project.notes.costOccurred}
                          </span>
                        ) : (
                          <span style={{ display: 'block', textAlign: 'center', color: 'var(--color-gray-400)' }}>
                            {isEditable ? '클릭하여 입력' : '-'}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            ) : (
              // V128.6: 리비전 있는 경우 - 4행 블록 구조
              <React.Fragment key={project.projectId}>
                {/* V64-1A: 프로젝트 간 공백 스페이서 (첫 프로젝트 제외) */}
                {!isFirstProject && (
                  <tr aria-hidden="true">
                    <td colSpan={21} style={{ height: '16px', padding: 0, backgroundColor: 'var(--color-gray-50)', border: 'none' }} />
                  </tr>
                )}
                {/* 블록 1행: 헤더1 */}
                {/* V64-1B: 프로젝트마다 상/하/좌/우 2px gray-500 박스 테두리 */}
                <tr style={{ backgroundColor: 'var(--color-gray-100)', borderTop: '2px solid var(--color-gray-500)' }}>
                  {/* V128.6: 프로젝트명 헤더 */}
                  <td style={{ ...inlineThStyle, borderLeft: '2px solid var(--color-gray-500)', borderRight: '1px solid var(--color-gray-300)' }}>프로젝트명</td>
                  {/* 헤더1 셀들: 기간~실투입MD (업무상세/특이사항/테스트 결제비용은 헤더2에서만 표시) */}
                  <td style={{ ...inlineThStyle }}>적용기간</td>
                  <td style={{ ...inlineThStyle, borderLeft: '1px solid var(--color-gray-300)' }}>WD</td>
                  <td style={{ ...inlineThStyle, borderLeft: '1px solid var(--color-gray-300)' }}>계약</td>
                  <td style={{ ...inlineThStyle }}>특</td>
                  <td style={{ ...inlineThStyle }}>고</td>
                  <td style={{ ...inlineThStyle }}>중</td>
                  <td style={{ ...inlineThStyle }}>초</td>
                  <td style={{ ...inlineThStyle }}>무</td>
                  <td style={{ ...inlineThStyle, borderLeft: '1px solid var(--color-gray-300)' }}>투입</td>
                  <td style={{ ...inlineThStyle }}>특</td>
                  <td style={{ ...inlineThStyle }}>고</td>
                  <td style={{ ...inlineThStyle }}>중</td>
                  <td style={{ ...inlineThStyle }}>초</td>
                  <td style={{ ...inlineThStyle, borderLeft: '1px solid var(--color-gray-300)' }}>기준MD</td>
                  {/* V128.2: 실투입MD colSpan=4 (업무상세/특이사항/테스트 결제비용 영역 흡수) */}
                  <td colSpan={4} style={{ ...inlineThStyle, borderLeft: '1px solid var(--color-gray-300)' }}>실투입MD</td>
                  {/* V205: 기준MM/실투입MM 컬럼 추가 (리비전 무관 전체 합산) */}
                  <td style={{ ...inlineThStyle, borderLeft: '1px solid var(--color-gray-300)' }}>기준MM</td>
                  <td style={{ ...inlineThStyle, borderLeft: '1px solid var(--color-gray-300)', borderRight: '2px solid var(--color-gray-500)' }}>실투입MM</td>
                </tr>

                {/* 블록 2~N+1행: 리비전 데이터 */}
                {/* V205.2: visibleSegments 기반 렌더링 */}
                {visibleSegments.map((seg, segIdx) => (
                  <tr
                    key={`${project.projectId}-${seg.revisionId}`}
                    style={{
                      // V64-1A: 리비전 데이터행 white 배경 — 흰 inset 카드 효과
                      backgroundColor: 'white',
                      borderBottom: segIdx === visibleSegments.length - 1 ? '1px solid var(--color-gray-300)' : '1px solid var(--color-gray-200)',
                    }}
                  >
                    {/* V128.6: 첫 번째 리비전 행에만 프로젝트명 값 (rowSpan = n + 2) */}
                    {segIdx === 0 && (
                      <td rowSpan={projectNameRowSpan} style={{ ...tdStyle, width: '180px', maxWidth: '180px', verticalAlign: 'middle', textAlign: 'center', borderLeft: '2px solid var(--color-gray-500)', borderRight: '1px solid var(--color-gray-300)', borderBottom: '2px solid var(--color-gray-500)' }}>
                        <button
                          type="button"
                          onClick={() => setSelectedProjectId(project.projectId)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '5px',
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            textAlign: 'center',
                          }}
                          title={`${project.projectName} 상세 보기`}
                        >
                          <span style={{
                            color: 'var(--color-primary-dark)',
                            textDecoration: 'underline',
                            textDecorationColor: 'var(--color-primary-light)',
                            fontSize: '15px',
                            wordBreak: 'break-word',
                          }}>
                            {project.projectName}
                          </span>
                        </button>
                      </td>
                    )}
                    {/* 기간 */}
                    <td style={{ ...tdStyle, textAlign: 'center', padding: '8px 4px' }}>
                      <span style={{
                        fontSize: '15px',
                        fontWeight: 500,
                        color: 'var(--color-gray-700)',
                      }}>
                        {formatPeriod(seg.effectiveFrom, seg.effectiveTo)}
                      </span>
                    </td>

                    {/* WD */}
                    <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600, padding: '8px 4px', borderLeft: '1px solid var(--color-gray-300)' }}>
                      {seg.workingDays}
                    </td>

                    {/* 계약인원 - 합계 */}
                    <td style={{
                      ...tdStyle,
                      textAlign: 'center',
                      fontWeight: 600,
                      padding: '8px 4px',
                      backgroundColor: seg.contractChanged ? 'var(--color-yellow-50)' : undefined,
                      borderLeft: '1px solid var(--color-gray-300)',
                    }}>
                      {seg.contractStaffCount}
                    </td>

                    {/* 계약 등급 개별 컬럼 */}
                    <td style={{ ...tdStyle, textAlign: 'center', fontSize: '18px', padding: '8px 4px', backgroundColor: seg.gradeChanged ? 'var(--color-yellow-50)' : undefined }}>
                      {formatGradeCount(seg.contractGradeCounts?.special)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center', fontSize: '18px', padding: '8px 4px', backgroundColor: seg.gradeChanged ? 'var(--color-yellow-50)' : undefined }}>
                      {formatGradeCount(seg.contractGradeCounts?.high)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center', fontSize: '18px', padding: '8px 4px', backgroundColor: seg.gradeChanged ? 'var(--color-yellow-50)' : undefined }}>
                      {formatGradeCount(seg.contractGradeCounts?.mid)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center', fontSize: '18px', padding: '8px 4px', backgroundColor: seg.gradeChanged ? 'var(--color-yellow-50)' : undefined }}>
                      {formatGradeCount(seg.contractGradeCounts?.low)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center', fontSize: '18px', padding: '8px 4px', backgroundColor: seg.gradeChanged ? 'var(--color-yellow-50)' : undefined }}>
                      {formatGradeCount((seg.contractGradeCounts as ContractGradeCounts)?.none)}
                    </td>

                    {/* 투입인원 - 합계 (계약 > 투입: 빨간색, 계약 < 투입: 초록색) */}
                    <td style={{
                      ...tdStyle,
                      textAlign: 'center',
                      fontWeight: 600,
                      padding: '8px 4px',
                      backgroundColor: seg.actualChanged ? 'var(--color-blue-50)' : undefined,
                      borderLeft: '1px solid var(--color-gray-300)',
                      color: seg.contractStaffCount > seg.actualStaffCount
                        ? 'var(--color-red-600)'
                        : seg.contractStaffCount < seg.actualStaffCount
                          ? 'var(--color-green-600)'
                          : undefined,
                    }}>
                      {seg.actualStaffCount}
                    </td>

                    {/* 투입 등급 개별 컬럼 */}
                    <td style={{ ...tdStyle, textAlign: 'center', fontSize: '18px', padding: '8px 4px', backgroundColor: seg.gradeChanged ? 'var(--color-blue-50)' : undefined }}>
                      {formatGradeCount(seg.actualGradeCounts?.special)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center', fontSize: '18px', padding: '8px 4px', backgroundColor: seg.gradeChanged ? 'var(--color-blue-50)' : undefined }}>
                      {formatGradeCount(seg.actualGradeCounts?.high)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center', fontSize: '18px', padding: '8px 4px', backgroundColor: seg.gradeChanged ? 'var(--color-blue-50)' : undefined }}>
                      {formatGradeCount(seg.actualGradeCounts?.mid)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center', fontSize: '18px', padding: '8px 4px', backgroundColor: seg.gradeChanged ? 'var(--color-blue-50)' : undefined }}>
                      {formatGradeCount(seg.actualGradeCounts?.low)}
                    </td>

                    {/* 기준MD */}
                    <td style={{ ...tdStyle, textAlign: 'center', padding: '8px 4px', borderLeft: '1px solid var(--color-gray-300)' }}>
                      {seg.baseMd.toFixed(1)}
                    </td>

                    {/* V128.2: 실투입MD colSpan=4 (업무상세/특이사항/테스트 결제비용 영역 흡수) */}
                    {/* V206.1: 모든 리비전 행에 개별 seg.actualMd 표시 (마지막 행도 합산값 아닌 개별값) */}
                    <td colSpan={4} style={{
                      ...tdStyle,
                      textAlign: 'center',
                      fontWeight: 600,
                      padding: '8px 4px',
                      borderLeft: '1px solid var(--color-gray-300)',
                      color: (seg.actualMd || seg.actualStaffCount * seg.workingDays) > seg.baseMd
                        ? 'var(--color-green-600)'
                        : (seg.actualMd || seg.actualStaffCount * seg.workingDays) < seg.baseMd
                          ? 'var(--color-red-600)'
                          : undefined,
                    }}>
                      {(seg.actualMd || seg.actualStaffCount * seg.workingDays).toFixed(1)}
                    </td>

                    {/* V210: 기준MM/실투입MM - 프로젝트 요약 1행에만 표시 (rowSpan으로 모든 리비전 커버) */}
                    {segIdx === 0 && (
                      <>
                        {/* V210: 기준MM (월 전체 WD 기반) */}
                        <td
                          rowSpan={revisionRowCount}
                          style={{
                            ...tdStyle,
                            textAlign: 'center',
                            padding: '8px 4px',
                            borderLeft: '1px solid var(--color-gray-300)',
                            fontWeight: 500,
                            color: 'var(--color-purple-600)',
                            verticalAlign: 'middle',
                          }}
                        >
                          {baseMm}
                        </td>
                        {/* V210: 실투입MM (월 전체 WD 기반, OT 포함) */}
                        <td
                          rowSpan={revisionRowCount}
                          style={{
                            ...tdStyle,
                            textAlign: 'center',
                            fontWeight: 600,
                            padding: '8px 4px',
                            borderLeft: '1px solid var(--color-gray-300)',
                            borderRight: '2px solid var(--color-gray-500)',
                            color: project.actualMd > project.baseMd
                              ? 'var(--color-green-600)'
                              : project.actualMd < project.baseMd
                                ? 'var(--color-red-600)'
                                : 'var(--color-purple-600)',
                            verticalAlign: 'middle',
                          }}
                        >
                          {actualMm}
                        </td>
                      </>
                    )}
                  </tr>
                ))}

                {/* V128.2: 블록 N+2행 헤더2 (추가근무MD | 등급별추가근무MD | 업무상세 | 특이사항 | 테스트 결제비용) */}
                {/* V129.4: td 자체 독립 너비를 위해 단일 td + flex 레이아웃 사용 */}
                {/* V132.1: 헤더-내용 세로 정렬 통일 (좌우 패딩 6px로 맞춤) */}
                <tr style={{ backgroundColor: 'var(--color-gray-100)' }}>
                  <td colSpan={20} style={{ padding: 0, borderRight: '2px solid var(--color-gray-500)' }}>
                    <div style={{ display: 'flex' }}>
                      <div style={{ width: '160px', ...inlineThStyle, padding: '6px 6px', boxSizing: 'border-box' }}>추가근무MD</div>
                      <div style={{ width: '200px', ...inlineThStyle, padding: '6px 6px', boxSizing: 'border-box' }}>등급별 추가근무MD</div>
                      <div style={{ flex: 5, ...inlineThStyle, padding: '6px 6px', boxSizing: 'border-box', borderLeft: '1px solid var(--color-gray-300)' }}>업무상세</div>
                      <div style={{ flex: 3, ...inlineThStyle, padding: '6px 6px', boxSizing: 'border-box', borderLeft: '1px solid var(--color-gray-300)' }}>특이사항</div>
                      <div style={{ flex: 2, ...inlineThStyle, padding: '6px 6px', boxSizing: 'border-box', borderLeft: '1px solid var(--color-gray-300)' }}>테스트 결제비용</div>
                    </div>
                  </td>
                </tr>

                {/* V128.3: 블록 N+3행 값2 */}
                {/* V129.4: td 자체 독립 너비를 위해 단일 td + flex 레이아웃 사용 */}
                {/* V130.2: 프로젝트 구분선 강화 - 하단 borderBottom 제거 (상단 borderTop으로 통일) */}
                {/* V132.1: boxSizing 추가로 헤더-내용 세로 정렬 통일 */}
                {/* V64-1B: 값2 행 white 배경 + 박스 우/하단 2px */}
                <tr style={{ backgroundColor: 'white' }}>
                  <td colSpan={20} style={{ padding: 0, borderRight: '2px solid var(--color-gray-500)', borderBottom: '2px solid var(--color-gray-500)' }}>
                    <div style={{ display: 'flex' }}>
                      {/* 추가근무MD (중앙정렬) - V206.2: overtimeByGrade 기반 계산 */}
                      <div style={{ width: '160px', ...tdStyle, boxSizing: 'border-box', textAlign: 'center', whiteSpace: 'nowrap', fontWeight: 600, color: project.overtimeMinutes > 0 ? 'var(--color-purple-600)' : undefined }}>
                        {getTotalOvertimeMd(project.overtimeByGrade)}
                      </div>
                      {/* 등급별 추가근무MD (중앙정렬) */}
                      <div style={{ width: '200px', ...tdStyle, boxSizing: 'border-box', textAlign: 'center', whiteSpace: 'nowrap', fontSize: '18px', color: 'var(--color-purple-600)' }}>
                        {formatOvertimeByGradeMultiline(project.overtimeByGrade)}
                      </div>
                      {/* V130: 업무상세 - 읽기 전용 클릭 영역 */}
                      {/* V204: memoTdStyle 적용 (기존 크기 유지) */}
                      <div
                        style={{
                          flex: 5,
                          ...memoTdStyle,
                          boxSizing: 'border-box',
                          textAlign: 'left',
                          verticalAlign: 'top',
                          borderLeft: '1px solid var(--color-gray-300)',
                          cursor: isEditable ? 'pointer' : undefined,
                          minHeight: '40px',
                          position: 'relative',
                          overflow: 'hidden',
                        }}
                        onClick={() => handleFieldClick(project, 'workDetails')}
                        title={isEditable ? '클릭하여 편집' : undefined}
                      >
                        {project.notes.workDetails ? (
                          <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {project.notes.workDetails}
                          </span>
                        ) : (
                          <span style={{ display: 'block', textAlign: 'center', color: 'var(--color-gray-400)' }}>
                            {isEditable ? '클릭하여 입력' : '-'}
                          </span>
                        )}
                      </div>
                      {/* V130: 특이사항 - 읽기 전용 클릭 영역 */}
                      {/* V204: memoTdStyle 적용 (기존 크기 유지) */}
                      <div
                        style={{
                          flex: 3,
                          ...memoTdStyle,
                          boxSizing: 'border-box',
                          textAlign: 'left',
                          verticalAlign: 'top',
                          borderLeft: '1px solid var(--color-gray-300)',
                          cursor: isEditable ? 'pointer' : undefined,
                          minHeight: '40px',
                          overflow: 'hidden',
                        }}
                        onClick={() => handleFieldClick(project, 'remarks')}
                        title={isEditable ? '클릭하여 편집' : undefined}
                      >
                        {project.notes.remarks ? (
                          <span style={{ color: 'var(--color-yellow-700)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {project.notes.remarks}
                          </span>
                        ) : (
                          <span style={{ display: 'block', textAlign: 'center', color: 'var(--color-gray-400)' }}>
                            {isEditable ? '클릭하여 입력' : '-'}
                          </span>
                        )}
                      </div>
                      {/* V130: 테스트 결제비용 - 읽기 전용 클릭 영역 */}
                      {/* V204: memoTdStyle 적용 (기존 크기 유지) */}
                      <div
                        style={{
                          flex: 2,
                          ...memoTdStyle,
                          boxSizing: 'border-box',
                          textAlign: 'left',
                          verticalAlign: 'top',
                          borderLeft: '1px solid var(--color-gray-300)',
                          cursor: isEditable ? 'pointer' : undefined,
                          minHeight: '40px',
                          overflow: 'hidden',
                        }}
                        onClick={() => handleFieldClick(project, 'costOccurred')}
                        title={isEditable ? '클릭하여 편집' : undefined}
                      >
                        {project.notes.costOccurred ? (
                          <span style={{ color: 'var(--color-green-700)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {project.notes.costOccurred}
                          </span>
                        ) : (
                          <span style={{ display: 'block', textAlign: 'center', color: 'var(--color-gray-400)' }}>
                            {isEditable ? '클릭하여 입력' : '-'}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      </div>

      {/* V116.6: 통합 Drawer 사용 (readOnly 모드) */}
      {selectedProjectId && (
        <ProjectDetailDrawer
          projectId={selectedProjectId}
          onClose={() => setSelectedProjectId(null)}
          readOnly={true}
        />
      )}

      {/* V130: 개별 필드 편집 모달 */}
      {modalState && (
        <MmNoteEditModal
          isOpen={modalState.isOpen}
          field={modalState.field}
          projectName={modalState.projectName}
          initialValue={modalState.initialValue}
          isSaving={isModalSaving}
          onSave={handleModalSave}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
}

// 스타일
const thStyle: React.CSSProperties = {
  padding: '10px 6px',
  textAlign: 'left',
  fontWeight: 600, // 볼드 처리
  color: 'var(--color-gray-700)',
  borderBottom: '3px solid var(--color-gray-400)',
  whiteSpace: 'nowrap',
  fontSize: '17px', // V116.1.4: +2 확대
};

// V128: 인라인 헤더 스타일 (프로젝트 블록 내 헤더1/헤더2용)
// V128.7: 볼드 처리 및 fontSize +4 (13px → 17px)
// V129.6: fontSize +2 (19px → 21px)
// V204: fontSize -4 (21px → 17px) - 프로젝트 현황 칼럼 축소
const inlineThStyle: React.CSSProperties = {
  padding: '6px 4px',
  textAlign: 'center',
  fontWeight: 700,
  color: 'var(--color-gray-600)',
  fontSize: '17px',
  whiteSpace: 'nowrap',
  backgroundColor: 'var(--color-gray-100)',
};

// V129.6: fontSize +2 (18px → 20px)
// V204: fontSize -4 (20px → 16px) - 프로젝트 현황 칼럼 축소
const tdStyle: React.CSSProperties = {
  padding: '8px 6px',
  verticalAlign: 'middle',
  fontSize: '16px',
};

// V204: 메모 칼럼 스타일 (업무상세/특이사항/테스트 결제비용) - 기존 크기 유지
const memoTdStyle: React.CSSProperties = {
  padding: '8px 6px',
  verticalAlign: 'middle',
  fontSize: '20px',
};
